import React, { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { Spinner } from "flowbite-react";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import Loading from "../../../../Utils/Loading/Loading";

const backend = await new Backend().init();

export default function Withdrawal() {
  const windowSize = useSize();

  const [amount, setAmount] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAmountModal, setShowAmountModal] = useState(true);
  const [loading, setLoading] = useState(false);

  const [upiId, setUpiId] = useState("");
  const [bankDetails, setBankDetails] = useState({
    accountNumber: "",
    ifscCode: "",
    accountName: "",
  });

  const handleAmountSubmit = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter valid amount");
    if (amt < 500) return toast.error("Minimum withdrawal ₹500 required");
    setShowAmountModal(false);
    setSelectedOption("upi");
  };

  const handleWithdrawal = useCallback(async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 500)
      return toast.error("Minimum withdrawal ₹500 required");

    setLoading(true);

    let data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      token: localStorage.getItem("token"),
      username: localStorage.getItem("username"),
      amount: amount,
    };

    if (selectedOption === "upi") {
      if (!upiId) return toast.error("Enter UPI ID");
      data.upi = upiId;
    } else {
      const { accountNumber, ifscCode, accountName } = bankDetails;
      if (!accountNumber || !ifscCode || !accountName)
        return toast.error("Enter all bank details");
      data.acc_name = accountName;
      data.acc_number = accountNumber;
      data.ifsc = ifscCode;
    }

    try {
      const r = await backend.withdrawal(data);
      if (r.error === "False") {
        toast.success(r.message);
        setAmount("");
        setUpiId("");
        setBankDetails({
          accountNumber: "",
          ifscCode: "",
          accountName: "",
        });
        setShowAmountModal(true);
      } else {
        toast.error(r.message);
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, [amount, selectedOption, upiId, bankDetails]);

  // =============================
  // Main UI
  // =============================

  const contentHeight = windowSize[1] - 90;
  const contentWidth = windowSize[0] - 30;

  // =============================
  // Loading Screen
  // =============================
  if (loading) {
    return <Loading height={windowSize[1] - 140} />;
  }

  return (
    <div
      style={{
        height: contentHeight,
      }}
      className="flex items-center justify-center p-8  bg-[var(--hover)]"
    >
      <div
        style={{
          width: contentWidth,
        }}
        className=" bg-[var(--background)] dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
      >
        {/* Step 1 */}
        {showAmountModal && (
          <div className="flex text-[var(--text)] flex-col gap-5">
            <h3 className="text-xl font-semibold text-center text-[var(--text)]">
              Enter Withdrawal Amount
            </h3>

            {/* Amount Input */}
            <input
              type="number"
              value={amount}
              placeholder="₹500 minimum"
              onChange={(e) => setAmount(e.target.value)}
              className="bg-[var(--background)] text-[var(--text)] border-2 border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
            />

            {/* Rules Section */}
            <div className="bg-yellow-50 dark:bg-gray-700 border border-yellow-300 dark:border-gray-600 rounded-xl p-4 text-sm text-gray-700 dark:text-gray-300">
              <h4 className="font-semibold mb-2 text-yellow-700 dark:text-yellow-400">
                ⚠ Withdrawal Rules
              </h4>

              <ul className="list-disc list-inside space-y-1">
                <li>
                  Withdrawal allowed only between <b>9:00 AM – 5:00 PM</b>
                </li>
                <li>
                  Withdrawals processed only on <b>Monday – Friday</b>
                </li>
                <li>
                  Minimum withdrawal amount is <b>₹500</b>
                </li>
                <li>All open trades must be closed before withdrawal</li>
                <li>Standing / P&L will be cleared after withdrawal</li>
              </ul>
            </div>

            {/* Continue Button */}
            <button
              onClick={handleAmountSubmit}
              className="w-full bg-[var(--primary)]  text-[var(--white)] font-semibold py-3 rounded-lg transition-all"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2 */}
        {!showAmountModal && (
          <>
            <div className="bg-[var(--primary)] text-[var(--white)] text-center py-3 rounded-lg mb-6 font-semibold">
              Withdrawal Amount: ₹{amount}
            </div>

            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setSelectedOption("upi")}
                className={`flex-1 py-2 rounded-lg border transition-all ${
                  selectedOption === "upi"
                    ? "bg-[var(--primary)] text-[var(--white)] border-blue-600"
                    : "bg-[var(--hover)] border-[var(--hover)]  text-[var(--primary)]"
                }`}
              >
                UPI
              </button>

              <button
                onClick={() => setSelectedOption("bank")}
                className={`flex-1 py-2 rounded-lg border transition-all ${
                  selectedOption === "bank"
                    ? "bg-[var(--primary)] text-[var(--white)] border-blue-600"
                    : "bg-[var(--hover)] border-[var(--hover)]  text-[var(--primary)]"
                }`}
              >
                Bank Transfer
              </button>
            </div>

            {selectedOption === "upi" && (
              <div className="flex justify-center items-center">
                <input
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="Enter UPI ID"
                  className="bg-[var(--background)]  mb-4 text-[var(--text)] border-2 border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                />
              </div>
            )}

            {selectedOption === "bank" && (
              <div className="flex flex-col gap-4 mb-4">
                <input
                  value={bankDetails.accountNumber}
                  onChange={(e) =>
                    setBankDetails({
                      ...bankDetails,
                      accountNumber: e.target.value,
                    })
                  }
                  placeholder="Account Number"
                  className="bg-[var(--background)] text-[var(--text)] border-2 border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                />
                <input
                  value={bankDetails.ifscCode}
                  onChange={(e) =>
                    setBankDetails({
                      ...bankDetails,
                      ifscCode: e.target.value,
                    })
                  }
                  placeholder="IFSC Code"
                  className="bg-[var(--background)] text-[var(--text)] border-2 border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                />
                <input
                  value={bankDetails.accountName}
                  onChange={(e) =>
                    setBankDetails({
                      ...bankDetails,
                      accountName: e.target.value,
                    })
                  }
                  placeholder="Account Name"
                  className="bg-[var(--background)] mb-4 text-[var(--text)] border-2 border-[--hover] rounded-lg p-2 text-center focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                />
              </div>
            )}

            {selectedOption && (
              <button
                onClick={handleWithdrawal}
                disabled={loading}
                className="w-full bg-[var(--primary)] text-[var(--white)] font-semibold py-3 rounded-lg flex justify-center items-center transition disabled:opacity-70"
              >
                {loading ? <Spinner size="sm" /> : "Submit Withdrawal"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
